/**
 * Employee Management React Query Hooks
 * Provides data fetching hooks for employees
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeApi, type EmployeeFilters, type CreateEmployeeData, type UpdateEmployeeData } from '../api/employees';

// Query keys for cache management
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: EmployeeFilters, page: number, limit: number) =>
    [...employeeKeys.lists(), { filters, page, limit }] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
  eligibleLeads: () => [...employeeKeys.all, 'eligible-leads'] as const,
};

/**
 * Hook to fetch paginated employees with filters
 */
export function useEmployees(filters: EmployeeFilters = {}, page = 1, limit = 20) {
  return useQuery({
    queryKey: employeeKeys.list(filters, page, limit),
    queryFn: async () => {
      const response = await employeeApi.getEmployees(filters, page, limit);
      return response.data;
    },
  });
}

/**
 * Hook to fetch single employee by ID
 */
export function useEmployee(id: string) {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: async () => {
      const response = await employeeApi.getEmployeeById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch eligible team leads (team_lead, manager, admin)
 */
export function useEligibleTeamLeads() {
  return useQuery({
    queryKey: employeeKeys.eligibleLeads(),
    queryFn: async () => {
      return await employeeApi.getEligibleTeamLeads();
    },
  });
}

/**
 * Hook to create a new employee
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEmployeeData) => {
      const response = await employeeApi.createEmployee(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all employee lists to refetch data
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.eligibleLeads() });
    },
  });
}

/**
 * Hook to update an existing employee
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEmployeeData }) => {
      const response = await employeeApi.updateEmployee(id, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate employee lists and specific detail
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.eligibleLeads() });
    },
  });
}

/**
 * Hook to deactivate an employee
 */
export function useDeactivateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await employeeApi.deactivateEmployee(id);
      return response.data;
    },
    onSuccess: (_, id) => {
      // Invalidate employee lists and specific detail
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
}
